import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { ClientAccountSelectModel, ClientPortfolio } from '../../common/store/types';
import { fetchCashAccounts, fetchCashStatements } from './thunks';
import { CashAccount, CashAccountsState, FetchCashStatementsResult } from './types';
import { DateRange, PagedRequest, QueryField } from 'src/store';
import { filterOperator } from 'src/common/components/dataTable/common';

export const buildDescriptionSearchPagination = (pagination: PagedRequest, searchTerm: string): PagedRequest => {
  const term = searchTerm.trim();
  const sortFields = pagination.queryFields.filter((field) => field.isSortTerm);
  const descriptionField: QueryField[] = term
    ? [
        {
          fieldName: 'description',
          hasSearchTerm: true,
          searchTerm: term,
          operator: filterOperator.contains,
          isSortTerm: false,
          descendingSortDirection: false,
        },
      ]
    : [];
  return {
    ...pagination,
    pageNumber: 1,
    queryFields: [...sortFields, ...descriptionField],
  };
};

export const initialState: CashAccountsState = {
  parameters: {
    portfolio: null,
    dates: {
      dateFrom: DateTime.now().minus({ days: 90 }).toISODate(),
      dateTo: DateTime.now().toISODate(),
      dateRangeType: '',
    },
    pagination: {
      pageNumber: 1,
      pageSize: 25,
      queryFields: [
        {
          fieldName: 'date',
          descendingSortDirection: true,
          hasSearchTerm: false,
          isSortTerm: true,
          searchTerm: '',
          operator: '',
        },
      ],
    },
    selectedAccountId: null,
    accountIdList: [],
  },
  cashAccounts: null,
  cashStatements: null,
  lastDataRefresh: null,
  showAudValues: false,
};

export const cashAccountsSlice = createSlice({
  name: '@@client/cashAccounts',
  initialState,
  reducers: {
    setPortfolioParameter: (state, action: PayloadAction<ClientPortfolio>) => {
      state.parameters.portfolio = action.payload;
    },
    setAccountParameter: (state, action: PayloadAction<ClientAccountSelectModel>) => {
      state.parameters.selectedAccountId = action.payload.accountId;
      state.parameters.accountIdList = action.payload.accountIdList;
    },
    setDateParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.dates = action.payload;
    },
    setPaginationParameter: (state, action: PayloadAction<PagedRequest>) => {
      state.parameters.pagination = action.payload;
    },
    setShowAudValues: (state, action: PayloadAction<boolean>) => {
      state.showAudValues = action.payload;
    },
    setDescriptionSearch: (state, action: PayloadAction<string>) => {
      state.parameters.pagination = buildDescriptionSearchPagination(state.parameters.pagination, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCashAccounts.pending, (state) => {
      state.cashAccounts = null;
    });
    builder.addCase(fetchCashAccounts.fulfilled, (state, action: PayloadAction<CashAccount[]>) => {
      state.cashAccounts = action.payload;
    });
    builder.addCase(fetchCashStatements.fulfilled, (state, action: PayloadAction<FetchCashStatementsResult>) => {
      state.cashStatements = action.payload.results;
      state.parameters.pagination = action.payload.pagination;
      state.lastDataRefresh = action.payload.lastDataRefresh;
    });
  },
});
